import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import API_URL from '../../../config';
import { useConfirmModal } from '../../../contexts/ConfirmModalContext';
import ContentPickerModal from '../ContentPickerModal/ContentPickerModal';
import './AutomationModal.css';

function AutomationModal({
    isOpen,
    onClose,
    editingSchedule,
    channels,
    tags,
    onSave,
    onDelete
}) {
    // Context hook
    const { showConfirm } = useConfirmModal();

    // Başlangıç form state'i (değişiklik kontrolü için)
    const initialFormRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [selectedTagSearch, setSelectedTagSearch] = useState('');
    const [selectedChannelSearch, setSelectedChannelSearch] = useState('');
    const [isTagSectionExpanded, setIsTagSectionExpanded] = useState(true);
    const [isChannelSectionExpanded, setIsChannelSectionExpanded] = useState(true);
    const [isYouTubeSectionExpanded, setIsYouTubeSectionExpanded] = useState(false);
    const [isXSectionExpanded, setIsXSectionExpanded] = useState(false);
    const [autoSort, setAutoSort] = useState(true);
    const [youtubeChannels, setYoutubeChannels] = useState([]);
    const [loadingYouTube, setLoadingYouTube] = useState(false);
    const [xAccounts, setXAccounts] = useState([]);
    const [loadingX, setLoadingX] = useState(false);
    const [scheduleMode, setScheduleMode] = useState('daily');
    const [tiktokAccounts, setTiktokAccounts] = useState([]);
    const [loadingTikTok, setLoadingTikTok] = useState(false);
    const [isTikTokSectionExpanded, setIsTikTokSectionExpanded] = useState(false);
    const [instagramAccounts, setInstagramAccounts] = useState([]);
    const [loadingInstagram, setLoadingInstagram] = useState(false);
    const [isInstagramSectionExpanded, setIsInstagramSectionExpanded] = useState(false);

    // Yeni etiket oluşturma state'leri
    const [showNewTagForm, setShowNewTagForm] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#667eea');
    const [creatingTag, setCreatingTag] = useState(false);

    // Local tags state (yeni eklenen etiketleri hemen göstermek için)
    const [localTags, setLocalTags] = useState([]);

    // Content Picker Modal state'leri
    const [showContentPicker, setShowContentPicker] = useState(false);
    const [orderedContents, setOrderedContents] = useState([]); // Sıralı içerik listesi
    const [contentOrderData, setContentOrderData] = useState([]); // API'ye gönderilecek sıra verisi

    const [scheduleForm, setScheduleForm] = useState({
        channel_ids: [],
        tag_ids: [],
        times_per_day: 1,
        daily_times: [{ hour: 9, minute: 0 }],
        monthly_schedules: [],
        is_active: true,
        youtube_channel_id: null,
        enable_youtube_upload: false,
        x_account_ids: [],
        tiktok_account_ids: [],
        enable_tiktok_upload: false,
        instagram_account_ids: [],
        enable_instagram_upload: false,
        content_order: 'random',
        stop_when_empty: false,
        enable_notifications: true
    });

    // Tags prop'u değiştiğinde local tags'ı güncelle
    useEffect(() => {
        setLocalTags(tags);
    }, [tags]);

    // YouTube kanallarını yükle
    useEffect(() => {
        if (isOpen) {
            loadYouTubeChannels();
            loadXAccounts();
            loadTikTokAccounts();
            loadInstagramAccounts();
        }
    }, [isOpen]);

    // Düzenleme modunda sıralı içerikleri yükle
    useEffect(() => {
        if (isOpen && editingSchedule && editingSchedule.content_order === 'sequential') {
            loadOrderedContents(editingSchedule.id);
        }
    }, [isOpen, editingSchedule]);

    // Sıralı içerikleri yükle
    const loadOrderedContents = async (ruleId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/automation/${ruleId}/content-order`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success && response.data.contents) {
                setOrderedContents(response.data.contents);
                setContentOrderData(response.data.contents.map((c, i) => ({
                    content_id: c.id,
                    order_index: i + 1
                })));
            }
        } catch (error) {
            console.error('Sıralı içerikler yüklenemedi:', error);
        }
    };

    // Modal açıldığında başlangıç state'ini kaydet
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                initialFormRef.current = JSON.stringify({
                    channel_ids: scheduleForm.channel_ids,
                    tag_ids: scheduleForm.tag_ids,
                    times_per_day: scheduleForm.times_per_day,
                    daily_times: scheduleForm.daily_times,
                    monthly_schedules: scheduleForm.monthly_schedules,
                    is_active: scheduleForm.is_active,
                    youtube_channel_id: scheduleForm.youtube_channel_id,
                    enable_youtube_upload: scheduleForm.enable_youtube_upload,
                    x_account_ids: scheduleForm.x_account_ids,
                    tiktok_account_ids: scheduleForm.tiktok_account_ids,
                    enable_tiktok_upload: scheduleForm.enable_tiktok_upload,
                    instagram_account_ids: scheduleForm.instagram_account_ids,
                    enable_instagram_upload: scheduleForm.enable_instagram_upload,
                    content_order: scheduleForm.content_order,
                    stop_when_empty: scheduleForm.stop_when_empty,
                    enable_notifications: scheduleForm.enable_notifications
                });
            }, 500);
        } else {
            initialFormRef.current = null;
        }
    }, [isOpen, editingSchedule]);

    const loadYouTubeChannels = async () => {
        setLoadingYouTube(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/youtube/channels`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // GetLate kanallarını map'le - ID formatını normalize et
            const channels = (response.data.channels || []).map(ch => {
                // GetLate kanalı için ID'yi belirle
                let finalId = ch.id;

                // GetLate kanalı mı kontrol et (birden fazla yöntemle)
                const isGetLateChannel = ch.isGetLate ||
                    !!ch.getlate_account_id ||
                    !!ch._id ||
                    (typeof ch.id === 'string' && ch.id.length === 24 && /^[a-f0-9]+$/i.test(ch.id));

                // Eğer GetLate kanalıysa ve id getlate_ ile başlamıyorsa, prefix ekle
                if (isGetLateChannel && !String(finalId).startsWith('getlate_')) {
                    const getlateId = ch._id || ch.getlate_account_id || ch.id;
                    finalId = `getlate_${getlateId}`;
                }

                console.log('🔍 Kanal ID işleme:', {
                    original: ch.id,
                    _id: ch._id,
                    isGetLate: isGetLateChannel,
                    final: finalId
                });

                return {
                    id: finalId,
                    _id: ch._id || ch.getlate_account_id || ch.id,
                    channel_id: ch.channel_id,
                    channel_name: ch.name || ch.channel_name || ch.displayName,
                    channel_url: ch.url || ch.channel_url,
                    channel_thumbnail: ch.thumbnail || ch.channel_thumbnail || ch.profilePicture,
                    subscriber_count: ch.stats?.subscribers || ch.subscriber_count || 0,
                    video_count: ch.stats?.videos || ch.video_count || 0,
                    view_count: ch.stats?.views || ch.view_count || 0,
                    is_active: ch.is_active !== false && ch.isActive !== false,
                    isGetLate: isGetLateChannel,
                    getlate_account_id: ch.getlate_account_id || ch._id || ch.id
                };
            });

            console.log('📺 YouTube kanalları yüklendi:', channels);
            setYoutubeChannels(channels);
        } catch (error) {
            console.error('YouTube kanalları yüklenemedi:', error);
        } finally {
            setLoadingYouTube(false);
        }
    };

    const loadXAccounts = async () => {
        setLoadingX(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/twitter/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // GetLate formatını normalize et
            const accounts = (response.data.accounts || []).map(acc => ({
                id: acc._id || acc.id,
                name: acc.displayName || acc.username || acc.name,
                username: acc.username,
                profile_image_url: acc.profilePicture || acc.profile_image_url,
                is_active: acc.isActive !== false,
                profileName: acc.profileName
            }));

            setXAccounts(accounts);
        } catch (error) {
            console.error('X hesapları yüklenemedi:', error);
        } finally {
            setLoadingX(false);
        }
    };

    const loadTikTokAccounts = async () => {
        setLoadingTikTok(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/tiktok/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setTiktokAccounts(response.data.accounts || []);
        } catch (error) {
            console.error('TikTok hesapları yüklenemedi:', error);
        } finally {
            setLoadingTikTok(false);
        }
    };

    const loadInstagramAccounts = async () => {
        setLoadingInstagram(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/instagram/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInstagramAccounts(response.data.accounts || []);
        } catch (error) {
            console.error('Instagram hesapları yüklenemedi:', error);
        } finally {
            setLoadingInstagram(false);
        }
    };

    // Yeni etiket oluşturma fonksiyonu
    const handleCreateTag = async () => {
        if (!newTagName.trim()) {
            toast.error('Lütfen etiket adı girin!');
            return;
        }

        setCreatingTag(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/tags`, {
                name: newTagName.trim(),
                color: newTagColor
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const newTag = response.data.tag;

            toast.success(`"${newTag.name}" etiketi oluşturuldu!`);

            // Yeni etiketi local tags'a ekle (hemen görünsün diye)
            setLocalTags(prevTags => [...prevTags, newTag]);

            // Yeni etiketi seçili etiketlere ekle
            setScheduleForm(prev => ({
                ...prev,
                tag_ids: [...prev.tag_ids, newTag.id]
            }));

            // Formu temizle ve kapat
            setNewTagName('');
            setNewTagColor('#667eea');
            setShowNewTagForm(false);

            // Yeni etikete scroll yap
            setTimeout(() => {
                const tagElements = document.querySelectorAll('.automation-tag-checkbox');
                const newTagElement = Array.from(tagElements).find(el => {
                    const tagNameDiv = el.querySelector('.automation-tag-name');
                    return tagNameDiv && tagNameDiv.textContent === newTag.name;
                });

                if (newTagElement) {
                    // Scroll yap
                    newTagElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Highlight efekti
                    newTagElement.style.transition = 'background 0.3s ease';
                    newTagElement.style.background = '#d1fae5';

                    setTimeout(() => {
                        newTagElement.style.background = '';
                    }, 2000);
                }
            }, 100);

        } catch (error) {
            console.error('❌ Etiket oluşturulamadı:', error);
            toast.error('Etiket oluşturulamadı: ' + (error.response?.data?.error || error.message));
        } finally {
            setCreatingTag(false);
        }
    };

    const sortTimesByHourMinute = (times) => {
        return [...times].sort((a, b) => {
            const timeA = a.hour * 60 + a.minute;
            const timeB = b.hour * 60 + b.minute;
            return timeA - timeB;
        });
    };

    useEffect(() => {
        if (editingSchedule && localTags.length > 0 && channels.length > 0) {
            console.log('🔄 Düzenleme için schedule yükleniyor:', editingSchedule);

            let channelIds = [];
            if (editingSchedule.channel_ids) {
                if (typeof editingSchedule.channel_ids === 'string') {
                    channelIds = editingSchedule.channel_ids.split(',').map(id => parseInt(id));
                } else if (Array.isArray(editingSchedule.channel_ids)) {
                    channelIds = editingSchedule.channel_ids;
                }
            } else if (editingSchedule.channel_id) {
                channelIds = [editingSchedule.channel_id];
            }

            let tagIds = [];
            if (editingSchedule.tags || editingSchedule.selected_tags) {
                const tagsString = editingSchedule.tags || editingSchedule.selected_tags;

                if (typeof tagsString === 'string' && tagsString.length > 0) {
                    const tagNames = tagsString.split(',').map(name => name.trim());
                    tagIds = tagNames.map(name => {
                        const tag = localTags.find(t => t.name === name);
                        return tag ? tag.id : null;
                    }).filter(id => id !== null);
                }
            }

            let xAccountIds = [];
            if (editingSchedule.x_account_ids) {
                if (typeof editingSchedule.x_account_ids === 'string') {
                    try {
                        xAccountIds = JSON.parse(editingSchedule.x_account_ids);
                    } catch {
                        xAccountIds = editingSchedule.x_account_ids.split(',').map(id => parseInt(id));
                    }
                } else if (Array.isArray(editingSchedule.x_account_ids)) {
                    xAccountIds = editingSchedule.x_account_ids;
                }
            }

            let tiktokAccountIds = [];
            if (editingSchedule.tiktok_account_ids) {
                if (typeof editingSchedule.tiktok_account_ids === 'string') {
                    try {
                        tiktokAccountIds = JSON.parse(editingSchedule.tiktok_account_ids);
                    } catch {
                        tiktokAccountIds = editingSchedule.tiktok_account_ids.split(',').map(id => id.trim());
                    }
                } else if (Array.isArray(editingSchedule.tiktok_account_ids)) {
                    tiktokAccountIds = editingSchedule.tiktok_account_ids;
                }
            }

            let instagramAccountIds = [];
            if (editingSchedule.instagram_account_ids) {
                if (typeof editingSchedule.instagram_account_ids === 'string') {
                    try {
                        instagramAccountIds = JSON.parse(editingSchedule.instagram_account_ids);
                    } catch {
                        instagramAccountIds = editingSchedule.instagram_account_ids.split(',').map(id => id.trim());
                    }
                } else if (Array.isArray(editingSchedule.instagram_account_ids)) {
                    instagramAccountIds = editingSchedule.instagram_account_ids;
                }
            }

            let mode = 'daily';
            let monthlySchedules = [];

            if (editingSchedule.schedule_mode === 'monthly' || editingSchedule.monthly_schedules) {
                mode = 'monthly';
                if (editingSchedule.monthly_schedules) {
                    try {
                        const parsed = typeof editingSchedule.monthly_schedules === 'string'
                            ? JSON.parse(editingSchedule.monthly_schedules)
                            : editingSchedule.monthly_schedules;
                        monthlySchedules = Array.isArray(parsed) ? parsed : [];
                    } catch (e) {
                        console.error('❌ Error parsing monthly_schedules:', e);
                    }
                }
            }

            let dailyTimes = [];

            if (editingSchedule.daily_times) {
                try {
                    const parsed = typeof editingSchedule.daily_times === 'string'
                        ? JSON.parse(editingSchedule.daily_times)
                        : editingSchedule.daily_times;
                    dailyTimes = Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('❌ Error parsing daily_times:', e);
                }
            }

            if (dailyTimes.length === 0) {
                const timesPerDay = editingSchedule.times_per_day || 1;
                for (let i = 0; i < timesPerDay; i++) {
                    const hour = Math.min(9 + (i * 2), 23);
                    dailyTimes.push({ hour, minute: 0 });
                }
            }

            const sortedTimes = autoSort ? sortTimesByHourMinute(dailyTimes) : dailyTimes;

            setScheduleMode(mode);

            const youtubeChannelId = editingSchedule.getlate_youtube_id || editingSchedule.youtube_channel_id || null;
            const hasYouTube = youtubeChannelId !== null;

            console.log('🎬 YouTube ID belirleme:', {
                getlate_youtube_id: editingSchedule.getlate_youtube_id,
                youtube_channel_id: editingSchedule.youtube_channel_id,
                final: youtubeChannelId
            });

            const newFormData = {
                channel_ids: channelIds,
                tag_ids: tagIds,
                times_per_day: sortedTimes.length,
                daily_times: sortedTimes,
                monthly_schedules: monthlySchedules,
                is_active: editingSchedule.is_active === 1,
                youtube_channel_id: youtubeChannelId,
                enable_youtube_upload: editingSchedule.enable_youtube_upload === 1 || hasYouTube,
                x_account_ids: xAccountIds,
                tiktok_account_ids: tiktokAccountIds,
                enable_tiktok_upload: editingSchedule.enable_tiktok_upload === 1,
                instagram_account_ids: instagramAccountIds,
                enable_instagram_upload: editingSchedule.enable_instagram_upload === 1,
                content_order: editingSchedule.content_order || 'random',
                stop_when_empty: editingSchedule.stop_when_empty === 1,
                enable_notifications: editingSchedule.enable_notifications !== 0
            };

            console.log('📝 Yeni form data:', newFormData);
            setScheduleForm(newFormData);

            if (editingSchedule.youtube_channel_id || editingSchedule.getlate_youtube_id) {
                setIsYouTubeSectionExpanded(true);
            }

            if (editingSchedule.x_account_ids && xAccountIds.length > 0) {
                setIsXSectionExpanded(true);
            }

            if (editingSchedule.tiktok_account_ids && tiktokAccountIds.length > 0) {
                setIsTikTokSectionExpanded(true);
            }

            if (editingSchedule.instagram_account_ids && instagramAccountIds.length > 0) {
                setIsInstagramSectionExpanded(true);
            }
        } else if (!editingSchedule) {
            resetForm();
        }
    }, [editingSchedule, localTags, channels]);

    const resetForm = () => {
        setScheduleForm({
            channel_ids: [],
            tag_ids: [],
            times_per_day: 1,
            daily_times: [{ hour: 9, minute: 0 }],
            monthly_schedules: [],
            is_active: true,
            youtube_channel_id: null,
            enable_youtube_upload: false,
            x_account_ids: [],
            tiktok_account_ids: [],
            enable_tiktok_upload: false,
            instagram_account_ids: [],
            enable_instagram_upload: false,
            content_order: 'random',
            stop_when_empty: false,
            enable_notifications: true
        });
        setScheduleMode('daily');
        setSelectedTagSearch('');
        setSelectedChannelSearch('');
        setIsTagSectionExpanded(true);
        setIsChannelSectionExpanded(true);
        setIsYouTubeSectionExpanded(false);
        setIsXSectionExpanded(false);
        setIsTikTokSectionExpanded(false);
        setIsInstagramSectionExpanded(false);
        setAutoSort(true);
        setShowNewTagForm(false);
        setNewTagName('');
        setNewTagColor('#667eea');
        // Sıralı içerik state'lerini temizle
        setOrderedContents([]);
        setContentOrderData([]);
    };

    // Content Picker'dan gelen veriyi kaydet
    const handleContentOrderSave = (orderData, contents) => {
        setContentOrderData(orderData);
        setOrderedContents(contents);
    };

    // İçerik sırası değiştiğinde sıralı içerikleri temizle
    const handleContentOrderChange = (newOrder) => {
        setScheduleForm({ ...scheduleForm, content_order: newOrder });
        if (newOrder === 'random') {
            setOrderedContents([]);
            setContentOrderData([]);
        }
    };

    // Form değişti mi kontrol et
    const hasFormChanged = () => {
        if (!initialFormRef.current) return false;

        const current = JSON.stringify({
            channel_ids: scheduleForm.channel_ids,
            tag_ids: scheduleForm.tag_ids,
            times_per_day: scheduleForm.times_per_day,
            daily_times: scheduleForm.daily_times,
            monthly_schedules: scheduleForm.monthly_schedules,
            is_active: scheduleForm.is_active,
            youtube_channel_id: scheduleForm.youtube_channel_id,
            enable_youtube_upload: scheduleForm.enable_youtube_upload,
            x_account_ids: scheduleForm.x_account_ids,
            tiktok_account_ids: scheduleForm.tiktok_account_ids,
            enable_tiktok_upload: scheduleForm.enable_tiktok_upload,
            instagram_account_ids: scheduleForm.instagram_account_ids,
            enable_instagram_upload: scheduleForm.enable_instagram_upload,
            content_order: scheduleForm.content_order,
            stop_when_empty: scheduleForm.stop_when_empty,
            enable_notifications: scheduleForm.enable_notifications
        });

        return current !== initialFormRef.current;
    };

    // Modal kapatma (değişiklik kontrolü ile)
    const handleClose = () => {
        if (hasFormChanged()) {
            showConfirm(async () => {
                onClose();
            });
        } else {
            onClose();
        }
    };

    const updateTimesPerDay = (newCount) => {
        if (newCount < 1) return;

        const currentTimes = [...scheduleForm.daily_times];
        const currentCount = currentTimes.length;

        if (newCount > currentCount) {
            for (let i = currentCount; i < newCount; i++) {
                let lastTime = currentTimes[currentTimes.length - 1] || { hour: 8, minute: 0 };
                let newHour = lastTime.hour + 1;

                if (newHour > 23) {
                    let availableHour = 0;
                    for (let h = 0; h < 24; h++) {
                        const isUsed = currentTimes.some(t => t.hour === h);
                        if (!isUsed) {
                            availableHour = h;
                            break;
                        }
                    }
                    newHour = availableHour;
                }

                currentTimes.push({
                    hour: newHour,
                    minute: lastTime.minute
                });
            }
        } else if (newCount < currentCount) {
            currentTimes.length = newCount;
        }

        const finalTimes = autoSort ? sortTimesByHourMinute(currentTimes) : currentTimes;

        setScheduleForm(prev => ({
            ...prev,
            times_per_day: newCount,
            daily_times: finalTimes
        }));
    };

    const handleTimeChange = (index, newTime) => {
        const [hour, minute] = newTime.split(':').map(Number);
        const updatedTimes = [...scheduleForm.daily_times];
        updatedTimes[index] = { hour, minute };

        // Yazarken sıralama yapma, sadece değeri güncelle
        setScheduleForm({
            ...scheduleForm,
            daily_times: updatedTimes
        });
    };

    // Blur olduğunda sırala
    const handleTimeBlur = () => {
        if (autoSort) {
            setScheduleForm(prev => ({
                ...prev,
                daily_times: sortTimesByHourMinute(prev.daily_times)
            }));
        }
    };

    const addMonthlySchedule = () => {
        const newSchedule = {
            day: 1,
            hour: 9,
            minute: 0
        };

        setScheduleForm({
            ...scheduleForm,
            monthly_schedules: [...scheduleForm.monthly_schedules, newSchedule]
        });
    };

    const removeMonthlySchedule = (index) => {
        const updatedSchedules = scheduleForm.monthly_schedules.filter((_, i) => i !== index);
        setScheduleForm({
            ...scheduleForm,
            monthly_schedules: updatedSchedules
        });
    };

    const updateMonthlySchedule = (index, field, value) => {
        const updatedSchedules = [...scheduleForm.monthly_schedules];

        if (field === 'time') {
            const [hour, minute] = value.split(':').map(Number);
            updatedSchedules[index] = {
                ...updatedSchedules[index],
                hour,
                minute
            };
        } else {
            updatedSchedules[index] = {
                ...updatedSchedules[index],
                [field]: parseInt(value)
            };
        }

        setScheduleForm({
            ...scheduleForm,
            monthly_schedules: updatedSchedules
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Platform kontrolü - en az bir platform seçilmiş olmalı
        const hasTelegram = scheduleForm.channel_ids.length > 0;
        const hasYouTube = scheduleForm.enable_youtube_upload && scheduleForm.youtube_channel_id;
        const hasX = scheduleForm.x_account_ids.length > 0;
        const hasTikTok = scheduleForm.enable_tiktok_upload && scheduleForm.tiktok_account_ids.length > 0;
        const hasInstagram = scheduleForm.enable_instagram_upload && scheduleForm.instagram_account_ids.length > 0;

        if (!hasTelegram && !hasYouTube && !hasX && !hasTikTok && !hasInstagram) {
            toast.error('Lütfen en az bir platform seçin! (Telegram, YouTube, X, TikTok veya Instagram)');
            return;
        }

        if (scheduleForm.tag_ids.length === 0) {
            toast.error('Lütfen en az bir etiket seçin!');
            return;
        }

        // Sıralı mod kontrolü
        if (scheduleForm.content_order === 'sequential' && contentOrderData.length === 0) {
            toast.error('Sıralı mod için lütfen içerikleri seçin ve sıralayın!');
            return;
        }

        // YouTube seçildiyse kanal kontrolü
        if (scheduleForm.enable_youtube_upload && !scheduleForm.youtube_channel_id) {
            toast.error('YouTube yükleme aktifse bir YouTube kanalı seçmelisiniz!');
            return;
        }

        if (scheduleForm.enable_tiktok_upload && scheduleForm.tiktok_account_ids.length === 0) {
            toast.error('TikTok paylaşımı aktifse en az bir TikTok hesabı seçmelisiniz!');
            return;
        }

        if (scheduleForm.enable_instagram_upload && scheduleForm.instagram_account_ids.length === 0) {
            toast.error('Instagram paylaşımı aktifse en az bir Instagram hesabı seçmelisiniz!');
            return;
        }

        if (scheduleMode === 'monthly') {
            if (scheduleForm.monthly_schedules.length === 0) {
                toast.error('Lütfen en az bir aylık zamanlama ekleyin!');
                return;
            }
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');

            const selectedTagNames = scheduleForm.tag_ids.map(tagId => {
                const tag = localTags.find(t => t.id === tagId);
                return tag ? tag.name : null;
            }).filter(name => name !== null);

            const sortedTimes = sortTimesByHourMinute(scheduleForm.daily_times);

            const data = {
                channel_ids: scheduleForm.channel_ids,
                selected_tags: selectedTagNames,
                frequency: 'daily_times',
                daily_times: JSON.stringify(sortedTimes),
                times_per_day: scheduleForm.times_per_day,
                is_active: scheduleForm.is_active ? 1 : 0,
                youtube_channel_id: scheduleForm.youtube_channel_id,
                enable_youtube_upload: scheduleForm.enable_youtube_upload,
                x_account_ids: scheduleForm.x_account_ids,
                schedule_mode: scheduleMode,
                tiktok_account_ids: scheduleForm.tiktok_account_ids,
                enable_tiktok_upload: scheduleForm.enable_tiktok_upload,
                instagram_account_ids: scheduleForm.instagram_account_ids,
                enable_instagram_upload: scheduleForm.enable_instagram_upload,
                monthly_schedules: scheduleMode === 'monthly' ? JSON.stringify(scheduleForm.monthly_schedules) : null,
                content_order: scheduleForm.content_order,
                stop_when_empty: scheduleForm.stop_when_empty,
                content_order_list: scheduleForm.content_order === 'sequential' ? contentOrderData : null,
                enable_notifications: scheduleForm.enable_notifications
            };

            console.log('📤 Gönderilen veri:', data);

            const fullUrl = editingSchedule
                ? `${API_URL}/automation/${editingSchedule.id}`
                : `${API_URL}/automation`;

            let response;
            if (editingSchedule) {
                response = await axios.put(fullUrl, data, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } else {
                response = await axios.post(fullUrl, data, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            console.log('✅ API Yanıtı:', response.data);

            onSave();
            onClose();
            toast.success(editingSchedule ? 'Otomasyon güncellendi!' : 'Otomasyon oluşturuldu!');
        } catch (error) {
            console.error('❌ Hata:', error);
            let errorMessage = 'Bilinmeyen hata';
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error('Otomasyon kaydedilemedi: ' + errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        toast((t) => (
            <div>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    Bu otomasyonu silmek istediğinizden emin misiniz?
                </p>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                    ⚠️ Bu işlem geri alınamaz!
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                const token = localStorage.getItem('token');
                                await axios.delete(`${API_URL}/automation/${editingSchedule.id}`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                });
                                toast.success('Otomasyon silindi!');
                                onDelete();
                                onClose();
                            } catch (error) {
                                console.error('❌ Otomasyon silinemedi:', error);
                                toast.error('Otomasyon silinemedi!');
                            }
                        }}
                        style={{
                            padding: '8px 16px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        Evet, Sil
                    </button>
                    <button
                        onClick={() => toast.dismiss(t.id)}
                        style={{
                            padding: '8px 16px',
                            background: '#6B7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        İptal
                    </button>
                </div>
            </div>
        ), {
            duration: Infinity,
            position: 'top-center',
            style: {
                minWidth: '350px',
                background: '#FFF',
                color: '#000',
                padding: '20px'
            }
        });
    };

    const filteredTags = localTags
        .filter(tag =>
            tag && tag.name && tag.name.toLowerCase().includes(selectedTagSearch.toLowerCase())
        )
        .sort((a, b) => {
            const aSelected = scheduleForm.tag_ids.includes(a.id) ? -1 : 1;
            const bSelected = scheduleForm.tag_ids.includes(b.id) ? -1 : 1;
            return aSelected - bSelected;
        });

    const filteredChannels = channels
        .filter(channel =>
            channel && channel.name && channel.name.toLowerCase().includes(selectedChannelSearch.toLowerCase())
        )
        .sort((a, b) => {
            const aSelected = scheduleForm.channel_ids.includes(a.id) ? -1 : 1;
            const bSelected = scheduleForm.channel_ids.includes(b.id) ? -1 : 1;
            return aSelected - bSelected;
        });

    if (!isOpen) return null;

    return (
        <div className="automation-modal-backdrop" onClick={handleClose}>
            <div className="automation-modal-content" onClick={e => e.stopPropagation()}>
                <div className="automation-modal-header">
                    <h3>
                        {editingSchedule ? '📝 Otomasyonu Düzenle' : '➕ Yeni Otomasyon'}
                    </h3>
                    <button className="automation-modal-close" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                Etiketler ({scheduleForm.tag_ids.length} seçili)
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsTagSectionExpanded(!isTagSectionExpanded)}
                            >
                                {isTagSectionExpanded ? '▼ Gizle' : '▶ Etiketleri Göster'}
                            </button>
                        </div>

                        {!isTagSectionExpanded && scheduleForm.tag_ids.length > 0 && (
                            <div className="automation-selected-preview">
                                {scheduleForm.tag_ids.map(tagId => {
                                    const tag = localTags.find(t => t.id === tagId);
                                    return tag ? (
                                        <span
                                            key={tag.id}
                                            className="automation-tag-preview-badge"
                                            style={{ backgroundColor: tag.color || '#667eea' }}
                                        >
                                            {tag.name}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isTagSectionExpanded ? 'expanded' : ''}`}>
                            {localTags.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz etiket oluşturulmamış. Lütfen önce İçerik Kütüphanesi'nden etiket ekleyin.
                                </div>
                            ) : (
                                <>

                                    {/* Arama ve Yeni Etiket Butonu */}
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                                        <input
                                            type="text"
                                            placeholder="Etiket ara..."
                                            value={selectedTagSearch}
                                            onChange={(e) => setSelectedTagSearch(e.target.value)}
                                            className="automation-search-input"
                                            style={{ flex: 1, marginBottom: 0 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewTagForm(!showNewTagForm)}
                                            style={{
                                                padding: '8px 12px',
                                                background: showNewTagForm ? '#6B7280' : '#10B981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                minWidth: '40px',
                                                transition: 'all 0.2s'
                                            }}
                                            title={showNewTagForm ? 'İptal' : 'Yeni Etiket Oluştur'}
                                        >
                                            {showNewTagForm ? '✕' : '+'}
                                        </button>
                                    </div>

                                    {/* Yeni Etiket Oluşturma Formu */}
                                    {showNewTagForm && (
                                        <div style={{
                                            marginBottom: '15px',
                                            padding: '15px',
                                            background: '#f0fdf4',
                                            border: '2px solid #10B981',
                                            borderRadius: '8px',
                                            animation: 'slideDown 0.2s ease-out'
                                        }}>
                                            <div style={{ marginBottom: '10px', fontWeight: '600', color: '#059669' }}>
                                                ✨ Yeni Etiket Oluştur
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Etiket adı..."
                                                    value={newTagName}
                                                    onChange={(e) => setNewTagName(e.target.value)}
                                                    maxLength={50}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px 12px',
                                                        border: '2px solid #10B981',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        outline: 'none'
                                                    }}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleCreateTag();
                                                        }
                                                    }}
                                                />

                                                <input
                                                    type="color"
                                                    value={newTagColor}
                                                    onChange={(e) => setNewTagColor(e.target.value)}
                                                    style={{
                                                        width: '50px',
                                                        height: '36px',
                                                        border: '2px solid #10B981',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Etiket rengi seç"
                                                />
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowNewTagForm(false);
                                                        setNewTagName('');
                                                        setNewTagColor('#667eea');
                                                    }}
                                                    disabled={creatingTag}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: '#E5E7EB',
                                                        color: '#374151',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    İptal
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCreateTag}
                                                    disabled={creatingTag || !newTagName.trim()}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: creatingTag || !newTagName.trim() ? '#9CA3AF' : '#10B981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: creatingTag || !newTagName.trim() ? 'not-allowed' : 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    {creatingTag ? 'Oluşturuluyor...' : '✓ Oluştur'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="automation-items-container automation-tags-container">
                                        {filteredTags.length === 0 ? (
                                            <div className="automation-no-items-message">
                                                Aramanızla eşleşen etiket bulunamadı
                                            </div>
                                        ) : (
                                            filteredTags.map(tag => (
                                                <label key={tag.id} className="automation-tag-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={scheduleForm.tag_ids.includes(tag.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setScheduleForm({
                                                                    ...scheduleForm,
                                                                    tag_ids: [...scheduleForm.tag_ids, tag.id]
                                                                });
                                                            } else {
                                                                setScheduleForm({
                                                                    ...scheduleForm,
                                                                    tag_ids: scheduleForm.tag_ids.filter(id => id !== tag.id)
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <div
                                                        className="automation-tag-color"
                                                        style={{ backgroundColor: tag.color || '#667eea' }}
                                                    ></div>
                                                    <div className="automation-tag-name">{tag.name}</div>
                                                </label>
                                            ))
                                        )}
                                    </div>

                                    <div className="automation-selection-actions">
                                        <button
                                            type="button"
                                            onClick={() => setScheduleForm({ ...scheduleForm, tag_ids: [] })}
                                            className="automation-btn-clear-all"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* KANAL SEÇİMİ */}
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                Telegram ({scheduleForm.channel_ids.length} seçili)
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsChannelSectionExpanded(!isChannelSectionExpanded)}
                            >
                                {isChannelSectionExpanded ? '▼ Gizle' : '▶ Kanalları Göster'}
                            </button>
                        </div>

                        {!isChannelSectionExpanded && scheduleForm.channel_ids.length > 0 && (
                            <div className="automation-selected-preview">
                                {scheduleForm.channel_ids.slice(0, 3).map(channelId => {
                                    const channel = channels.find(c => c.id === channelId);
                                    return channel ? (
                                        <span key={channel.id} className="automation-preview-badge">
                                            {channel.name}
                                        </span>
                                    ) : null;
                                })}
                                {scheduleForm.channel_ids.length > 3 && (
                                    <span className="automation-preview-badge more">
                                        +{scheduleForm.channel_ids.length - 3} kanal
                                    </span>
                                )}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isChannelSectionExpanded ? 'expanded' : ''}`}>
                            {channels.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz kanal eklenmemiş. Lütfen önce Kanallar sekmesinden kanal ekleyin.
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Kanal ara..."
                                        value={selectedChannelSearch}
                                        onChange={(e) => setSelectedChannelSearch(e.target.value)}
                                        className="automation-search-input"
                                    />

                                    <div className="automation-items-container automation-channels-container">
                                        {filteredChannels.length === 0 ? (
                                            <div className="automation-no-items-message">
                                                Aramanızla eşleşen kanal bulunamadı
                                            </div>
                                        ) : (
                                            filteredChannels.map(channel => {
                                                const profilePhotoUrl = channel.profile_photo
                                                    ? `https://api.autogram.site/uploads/${channel.profile_photo}`
                                                    : null;

                                                return (
                                                    <label key={channel.id} className="automation-channel-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={scheduleForm.channel_ids.includes(channel.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        channel_ids: [...scheduleForm.channel_ids, channel.id]
                                                                    });
                                                                } else {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        channel_ids: scheduleForm.channel_ids.filter(id => id !== channel.id)
                                                                    });
                                                                }
                                                            }}
                                                        />

                                                        <div className="automation-channel-avatar">
                                                            {profilePhotoUrl ? (
                                                                <>
                                                                    <img
                                                                        src={profilePhotoUrl}
                                                                        alt={channel.name}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            const fallback = e.target.parentElement.querySelector('.automation-channel-avatar-fallback');
                                                                            if (fallback) fallback.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                    <div
                                                                        className="automation-channel-avatar-fallback"
                                                                        style={{ display: 'none' }}
                                                                    >
                                                                        {channel.name ? channel.name.charAt(0).toUpperCase() : '📡'}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="automation-channel-avatar-fallback" style={{ display: 'flex' }}>
                                                                    {channel.name ? channel.name.charAt(0).toUpperCase() : '📡'}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="automation-channel-info">
                                                            <div className="automation-channel-name">{channel.name}</div>
                                                            {channel.member_count > 0 && (
                                                                <div className="automation-channel-members">
                                                                    👥 {channel.member_count} üye
                                                                </div>
                                                            )}
                                                        </div>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>

                                    <div className="automation-selection-actions">
                                        <button
                                            type="button"
                                            onClick={() => setScheduleForm({ ...scheduleForm, channel_ids: [] })}
                                            className="automation-btn-clear-all"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ETİKET SEÇİMİ */}


                    {/* YOUTUBE KANAL SEÇİMİ */}
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                YouTube
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsYouTubeSectionExpanded(!isYouTubeSectionExpanded)}
                            >
                                {isYouTubeSectionExpanded ? '▼ Gizle' : '▶ YouTube Ayarlarını Göster'}
                            </button>
                        </div>

                        {!isYouTubeSectionExpanded && scheduleForm.youtube_channel_id && (
                            <div className="automation-selected-preview">
                                {(() => {
                                    // ID'yi normalize et (getlate_ prefix'i olabilir veya olmayabilir)
                                    const normalizedFormId = String(scheduleForm.youtube_channel_id);
                                    const selectedYouTube = youtubeChannels.find(ch => {
                                        const normalizedChId = String(ch.id);
                                        return normalizedChId === normalizedFormId ||
                                            ch._id === normalizedFormId ||
                                            ch.getlate_account_id === normalizedFormId ||
                                            `getlate_${ch._id}` === normalizedFormId ||
                                            `getlate_${ch.getlate_account_id}` === normalizedFormId;
                                    });
                                    return selectedYouTube ? (
                                        <span className="automation-preview-badge" style={{ background: '#FF0000', color: 'white' }}>
                                            📺 {selectedYouTube.channel_name}
                                        </span>
                                    ) : null;
                                })()}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isYouTubeSectionExpanded ? 'expanded' : ''}`}>
                            {loadingYouTube ? (
                                <div className="automation-info-message">
                                    YouTube kanalları yükleniyor...
                                </div>
                            ) : youtubeChannels.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz YouTube kanalı bağlanmamış. YouTube sekmesinden kanal ekleyebilirsiniz.
                                </div>
                            ) : (
                                <>
                                    <label className="automation-checkbox-label" style={{ marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={scheduleForm.enable_youtube_upload}
                                            onChange={(e) => {
                                                setScheduleForm({
                                                    ...scheduleForm,
                                                    enable_youtube_upload: e.target.checked,
                                                    youtube_channel_id: e.target.checked ? scheduleForm.youtube_channel_id : null
                                                });
                                            }}
                                            className="automation-checkbox-input"
                                        />
                                        <span className="automation-checkbox-text">
                                            YouTube'a otomatik yükleme yap
                                        </span>
                                    </label>

                                    {scheduleForm.enable_youtube_upload && (
                                        <div className="automation-youtube-channels-list">
                                            {youtubeChannels
                                                .sort((a, b) => {
                                                    const normalizedFormId = String(scheduleForm.youtube_channel_id || '');
                                                    const aMatch = String(a.id) === normalizedFormId || a._id === normalizedFormId || `getlate_${a._id}` === normalizedFormId ? -1 : 1;
                                                    const bMatch = String(b.id) === normalizedFormId || b._id === normalizedFormId || `getlate_${b._id}` === normalizedFormId ? -1 : 1;
                                                    return aMatch - bMatch;
                                                })
                                                .map(channel => {
                                                    // Seçili mi kontrol et - farklı ID formatlarını destekle
                                                    const normalizedFormId = String(scheduleForm.youtube_channel_id || '');
                                                    const normalizedChId = String(channel.id || '');
                                                    const isSelected = normalizedFormId === normalizedChId ||
                                                        normalizedFormId === channel._id ||
                                                        normalizedFormId === channel.getlate_account_id ||
                                                        normalizedFormId === `getlate_${channel._id}` ||
                                                        normalizedFormId === `getlate_${channel.getlate_account_id}` ||
                                                        `getlate_${normalizedFormId}` === normalizedChId;

                                                    return (
                                                        <label key={channel.id} className="automation-youtube-channel-item">
                                                            <input
                                                                type="radio"
                                                                name="youtube_channel"
                                                                checked={isSelected}
                                                                onChange={() => {
                                                                    console.log('📺 YouTube kanal seçildi:', channel.id);
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        youtube_channel_id: channel.id
                                                                    });
                                                                }}
                                                            />

                                                            <div className="automation-youtube-channel-info">
                                                                {channel.channel_thumbnail && (
                                                                    <img
                                                                        src={channel.channel_thumbnail}
                                                                        alt={channel.channel_name}
                                                                        className="automation-youtube-thumbnail"
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            borderRadius: '50%',
                                                                            marginRight: '10px'
                                                                        }}
                                                                    />
                                                                )}
                                                                <div>
                                                                    <div className="automation-youtube-channel-name" style={{ fontWeight: 'bold' }}>
                                                                        {channel.channel_name}
                                                                    </div>
                                                                    <div className="automation-youtube-channel-stats" style={{ fontSize: '12px', color: '#666' }}>
                                                                        👥 {(channel.subscriber_count || 0).toLocaleString('tr-TR')} abone
                                                                        {' • '}
                                                                        📹 {(channel.video_count || 0).toLocaleString('tr-TR')} video
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                        </div>
                                    )}

                                    {scheduleForm.enable_youtube_upload && !scheduleForm.youtube_channel_id && youtubeChannels.length > 0 && (
                                        <div style={{ color: '#EF4444', fontSize: '13px', marginTop: '10px' }}>
                                            ⚠️ Lütfen bir YouTube kanalı seçin
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* X (TWITTER) HESAP SEÇİMİ */}
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                X (Twitter) {scheduleForm.x_account_ids.length > 0 && `(${scheduleForm.x_account_ids.length} seçili)`}
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsXSectionExpanded(!isXSectionExpanded)}
                            >
                                {isXSectionExpanded ? '▼ Gizle' : '▶ X Ayarlarını Göster'}
                            </button>
                        </div>

                        {!isXSectionExpanded && scheduleForm.x_account_ids.length > 0 && (
                            <div className="automation-selected-preview">
                                {scheduleForm.x_account_ids.map(accountId => {
                                    const account = xAccounts.find(acc => acc.id === accountId);
                                    return account ? (
                                        <span key={account.id} className="automation-preview-badge" style={{ background: '#000', color: 'white' }}>
                                            @{account.username}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isXSectionExpanded ? 'expanded' : ''}`}>
                            {loadingX ? (
                                <div className="automation-info-message">
                                    X hesapları yükleniyor...
                                </div>
                            ) : xAccounts.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz X hesabı bağlanmamış. X sekmesinden hesap ekleyebilirsiniz.
                                </div>
                            ) : (
                                <>
                                    <div className="automation-x-accounts-list" style={{ marginTop: '15px' }}>
                                        {xAccounts
                                            .sort((a, b) => {
                                                const aSelected = scheduleForm.x_account_ids.includes(a.id) ? -1 : 1;
                                                const bSelected = scheduleForm.x_account_ids.includes(b.id) ? -1 : 1;
                                                return aSelected - bSelected;
                                            })
                                            .map(account => (
                                                <label key={account.id} className="automation-x-account-item" style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px',
                                                    marginBottom: '8px',
                                                    background: '#f9fafb',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e5e7eb',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.2s'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={scheduleForm.x_account_ids.includes(account.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setScheduleForm({
                                                                    ...scheduleForm,
                                                                    x_account_ids: [...scheduleForm.x_account_ids, account.id]
                                                                });
                                                            } else {
                                                                setScheduleForm({
                                                                    ...scheduleForm,
                                                                    x_account_ids: scheduleForm.x_account_ids.filter(id => id !== account.id)
                                                                });
                                                            }
                                                        }}
                                                        style={{ marginRight: '12px' }}
                                                    />

                                                    {account.profile_image_url && (
                                                        <img
                                                            src={account.profile_image_url}
                                                            alt={account.name}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '50%',
                                                                marginRight: '10px'
                                                            }}
                                                        />
                                                    )}

                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            {account.name}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            @{account.username}
                                                            {account.followers_count && (
                                                                <span> • {account.followers_count.toLocaleString('tr-TR')} takipçi</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        {account.is_active ? (
                                                            <span style={{ color: '#10B981', fontSize: '12px', fontWeight: '500' }}>✅ Aktif</span>
                                                        ) : (
                                                            <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: '500' }}>❌ Pasif</span>
                                                        )}
                                                    </div>
                                                </label>
                                            ))}
                                    </div>

                                    <div className="automation-selection-actions" style={{ marginTop: '10px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setScheduleForm({ ...scheduleForm, x_account_ids: [] })}
                                            className="automation-btn-clear-all"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* TIKTOK HESAP SEÇİMİ */}
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                TikTok {scheduleForm.tiktok_account_ids.length > 0 && `(${scheduleForm.tiktok_account_ids.length} seçili)`}
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsTikTokSectionExpanded(!isTikTokSectionExpanded)}
                            >
                                {isTikTokSectionExpanded ? '▼ Gizle' : '▶ TikTok Ayarlarını Göster'}
                            </button>
                        </div>

                        {!isTikTokSectionExpanded && scheduleForm.tiktok_account_ids.length > 0 && (
                            <div className="automation-selected-preview">
                                {scheduleForm.tiktok_account_ids.map(accountId => {
                                    const account = tiktokAccounts.find(acc => acc._id === accountId);
                                    return account ? (
                                        <span key={account._id} className="automation-preview-badge" style={{ background: '#FE2C55', color: 'white' }}>
                                            @{account.username}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isTikTokSectionExpanded ? 'expanded' : ''}`}>
                            {loadingTikTok ? (
                                <div className="automation-info-message">
                                    TikTok hesapları yükleniyor...
                                </div>
                            ) : tiktokAccounts.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz TikTok hesabı bağlanmamış. TikTok sekmesinden hesap ekleyebilirsiniz.
                                </div>
                            ) : (
                                <>
                                    <label className="automation-checkbox-label" style={{ marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={scheduleForm.enable_tiktok_upload}
                                            onChange={(e) => {
                                                setScheduleForm({
                                                    ...scheduleForm,
                                                    enable_tiktok_upload: e.target.checked,
                                                    tiktok_account_ids: e.target.checked ? scheduleForm.tiktok_account_ids : []
                                                });
                                            }}
                                            className="automation-checkbox-input"
                                        />
                                        <span className="automation-checkbox-text">
                                            TikTok'a otomatik video paylaş
                                        </span>
                                    </label>

                                    {scheduleForm.enable_tiktok_upload && (
                                        <div className="automation-tiktok-accounts-list" style={{ marginTop: '15px' }}>
                                            {tiktokAccounts
                                                .sort((a, b) => {
                                                    const aSelected = scheduleForm.tiktok_account_ids.includes(a._id) ? -1 : 1;
                                                    const bSelected = scheduleForm.tiktok_account_ids.includes(b._id) ? -1 : 1;
                                                    return aSelected - bSelected;
                                                })
                                                .map(account => (
                                                    <label key={account._id} className="automation-tiktok-account-item" style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px',
                                                        marginBottom: '8px',
                                                        background: '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e5e7eb',
                                                        cursor: 'pointer',
                                                        transition: 'background 0.2s'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={scheduleForm.tiktok_account_ids.includes(account._id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        tiktok_account_ids: [...scheduleForm.tiktok_account_ids, account._id]
                                                                    });
                                                                } else {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        tiktok_account_ids: scheduleForm.tiktok_account_ids.filter(id => id !== account._id)
                                                                    });
                                                                }
                                                            }}
                                                            style={{ marginRight: '12px' }}
                                                        />

                                                        {account.profilePicture && (
                                                            <img
                                                                src={account.profilePicture}
                                                                alt={account.displayName || account.username}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    marginRight: '10px'
                                                                }}
                                                            />
                                                        )}

                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 'bold' }}>
                                                                {account.displayName || account.username}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                @{account.username}
                                                                {account.profileName && (
                                                                    <span> • {account.profileName}</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            {account.isActive ? (
                                                                <span style={{ color: '#10B981', fontSize: '12px', fontWeight: '500' }}>✅ Aktif</span>
                                                            ) : (
                                                                <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: '500' }}>❌ Pasif</span>
                                                            )}
                                                        </div>
                                                    </label>
                                                ))}
                                        </div>
                                    )}

                                    {scheduleForm.enable_tiktok_upload && scheduleForm.tiktok_account_ids.length === 0 && tiktokAccounts.length > 0 && (
                                        <div style={{ color: '#EF4444', fontSize: '13px', marginTop: '10px' }}>
                                            ⚠️ Lütfen en az bir TikTok hesabı seçin
                                        </div>
                                    )}

                                    <div className="automation-selection-actions" style={{ marginTop: '10px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setScheduleForm({ ...scheduleForm, tiktok_account_ids: [] })}
                                            className="automation-btn-clear-all"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* INSTAGRAM HESAP SEÇİMİ */}
                    <div className="automation-form-group">
                        <div className="automation-section-header">
                            <label className="automation-form-label">
                                Instagram {scheduleForm.instagram_account_ids.length > 0 && `(${scheduleForm.instagram_account_ids.length} seçili)`}
                            </label>
                            <button
                                type="button"
                                className="automation-section-toggle-btn"
                                onClick={() => setIsInstagramSectionExpanded(!isInstagramSectionExpanded)}
                            >
                                {isInstagramSectionExpanded ? '▼ Gizle' : '▶ Instagram Ayarlarını Göster'}
                            </button>
                        </div>

                        {!isInstagramSectionExpanded && scheduleForm.instagram_account_ids.length > 0 && (
                            <div className="automation-selected-preview">
                                {scheduleForm.instagram_account_ids.map(accountId => {
                                    const account = instagramAccounts.find(acc => acc._id === accountId);
                                    return account ? (
                                        <span key={account._id} className="automation-preview-badge" style={{
                                            background: 'linear-gradient(45deg, #833AB4, #FD1D1D, #FCB045)',
                                            color: 'white'
                                        }}>
                                            @{account.username}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}

                        <div className={`automation-selection-container ${isInstagramSectionExpanded ? 'expanded' : ''}`}>
                            {loadingInstagram ? (
                                <div className="automation-info-message">
                                    Instagram hesapları yükleniyor...
                                </div>
                            ) : instagramAccounts.length === 0 ? (
                                <div className="automation-warning-message">
                                    ⚠️ Henüz Instagram hesabı bağlanmamış. Instagram sekmesinden hesap ekleyebilirsiniz.
                                </div>
                            ) : (
                                <>

                                    <label className="automation-checkbox-label" style={{ marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={scheduleForm.enable_instagram_upload}
                                            onChange={(e) => {
                                                setScheduleForm({
                                                    ...scheduleForm,
                                                    enable_instagram_upload: e.target.checked,
                                                    instagram_account_ids: e.target.checked ? scheduleForm.instagram_account_ids : []
                                                });
                                            }}
                                            className="automation-checkbox-input"
                                        />
                                        <span className="automation-checkbox-text">
                                            Instagram'a otomatik paylaşım yap
                                        </span>
                                    </label>

                                    {scheduleForm.enable_instagram_upload && (
                                        <div className="automation-instagram-accounts-list" style={{ marginTop: '15px' }}>
                                            {instagramAccounts
                                                .sort((a, b) => {
                                                    const aSelected = scheduleForm.instagram_account_ids.includes(a._id) ? -1 : 1;
                                                    const bSelected = scheduleForm.instagram_account_ids.includes(b._id) ? -1 : 1;
                                                    return aSelected - bSelected;
                                                })
                                                .map(account => (
                                                    <label key={account._id} className="automation-instagram-account-item" style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px',
                                                        marginBottom: '8px',
                                                        background: '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e5e7eb',
                                                        cursor: 'pointer',
                                                        transition: 'background 0.2s'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={scheduleForm.instagram_account_ids.includes(account._id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        instagram_account_ids: [...scheduleForm.instagram_account_ids, account._id]
                                                                    });
                                                                } else {
                                                                    setScheduleForm({
                                                                        ...scheduleForm,
                                                                        instagram_account_ids: scheduleForm.instagram_account_ids.filter(id => id !== account._id)
                                                                    });
                                                                }
                                                            }}
                                                            style={{ marginRight: '12px' }}
                                                        />

                                                        {account.profilePicture && (
                                                            <img
                                                                src={account.profilePicture}
                                                                alt={account.displayName || account.username}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    marginRight: '10px'
                                                                }}
                                                            />
                                                        )}

                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 'bold' }}>
                                                                {account.displayName || account.username}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                @{account.username}
                                                                {account.profileName && (
                                                                    <span> • {account.profileName}</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            {account.isActive ? (
                                                                <span style={{ color: '#10B981', fontSize: '12px', fontWeight: '500' }}>✅ Aktif</span>
                                                            ) : (
                                                                <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: '500' }}>❌ Pasif</span>
                                                            )}
                                                        </div>
                                                    </label>
                                                ))}
                                        </div>
                                    )}

                                    {scheduleForm.enable_instagram_upload && scheduleForm.instagram_account_ids.length === 0 && instagramAccounts.length > 0 && (
                                        <div style={{ color: '#EF4444', fontSize: '13px', marginTop: '10px' }}>
                                            ⚠️ Lütfen en az bir Instagram hesabı seçin
                                        </div>
                                    )}

                                    <div className="automation-selection-actions" style={{ marginTop: '10px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setScheduleForm({ ...scheduleForm, instagram_account_ids: [] })}
                                            className="automation-btn-clear-all"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* İÇERİK SIRASI VE DURDURMA AYARLARI */}
                    <div className="automation-form-group">
                        <label className="automation-form-label">
                            İçerik Seçim Ayarları
                        </label>

                        <div style={{
                            marginTop: '10px',
                            padding: '15px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            {/* İçerik Sırası */}
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                                    İçerik Sırası
                                </label>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: '10px 15px',
                                        background: scheduleForm.content_order === 'random' ? '#EEF2FF' : 'white',
                                        border: scheduleForm.content_order === 'random' ? '2px solid #6366F1' : '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        flex: 1,
                                        transition: 'all 0.2s'
                                    }}>
                                        <input
                                            type="radio"
                                            name="content_order"
                                            value="random"
                                            checked={scheduleForm.content_order === 'random'}
                                            onChange={(e) => handleContentOrderChange(e.target.value)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: scheduleForm.content_order === 'random' ? '600' : '400' }}>
                                                🎲 Rastgele
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                Her paylaşımda rastgele içerik seçilir
                                            </div>
                                        </div>
                                    </label>

                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: '10px 15px',
                                        background: scheduleForm.content_order === 'sequential' ? '#EEF2FF' : 'white',
                                        border: scheduleForm.content_order === 'sequential' ? '2px solid #6366F1' : '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        flex: 1,
                                        transition: 'all 0.2s'
                                    }}>
                                        <input
                                            type="radio"
                                            name="content_order"
                                            value="sequential"
                                            checked={scheduleForm.content_order === 'sequential'}
                                            onChange={(e) => handleContentOrderChange(e.target.value)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: scheduleForm.content_order === 'sequential' ? '600' : '400' }}>
                                                📋 Sıralı
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                Belirlediğiniz sırayla paylaşılır
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Sıralı Mod Seçiliyse - İçerik Seçim Butonu */}
                            {scheduleForm.content_order === 'sequential' && (
                                <div style={{
                                    padding: '15px',
                                    background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                                    borderRadius: '8px',
                                    border: '2px solid #6366F1',
                                    marginBottom: '15px'
                                }}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontWeight: '600', color: '#4338CA', marginBottom: '4px' }}>
                                            📋 Sıralı İçerik Modu
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#6366F1' }}>
                                            {scheduleForm.tag_ids.length === 0
                                                ? '⚠️ Önce yukarıdan en az bir etiket seçin'
                                                : orderedContents.length > 0
                                                    ? `✅ ${orderedContents.length} içerik sıralandı`
                                                    : '👇 İçerikleri seçip sıralayın'
                                            }
                                        </div>
                                    </div>

                                    {/* Seçili içerik önizlemesi */}
                                    {orderedContents.length > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            marginBottom: '12px',
                                            maxHeight: '100px',
                                            overflowY: 'auto',
                                            padding: '8px',
                                            background: 'white',
                                            borderRadius: '6px'
                                        }}>
                                            {orderedContents.slice(0, 10).map((content, index) => (
                                                <div
                                                    key={content.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '4px 10px',
                                                        background: '#F3F4F6',
                                                        borderRadius: '20px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    <span style={{
                                                        background: '#6366F1',
                                                        color: 'white',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                        fontWeight: '700'
                                                    }}>
                                                        {index + 1}
                                                    </span>
                                                    <span style={{
                                                        maxWidth: '120px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {content.title || 'Başlıksız'}
                                                    </span>
                                                </div>
                                            ))}
                                            {orderedContents.length > 10 && (
                                                <div style={{
                                                    padding: '4px 10px',
                                                    background: '#E5E7EB',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    color: '#6B7280'
                                                }}>
                                                    +{orderedContents.length - 10} daha
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => setShowContentPicker(true)}
                                        disabled={scheduleForm.tag_ids.length === 0}
                                        style={{
                                            width: '100%',
                                            padding: '12px 20px',
                                            background: scheduleForm.tag_ids.length === 0
                                                ? '#9CA3AF'
                                                : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: scheduleForm.tag_ids.length === 0 ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {orderedContents.length > 0 ? (
                                            <>📝 Sıralamayı Düzenle</>
                                        ) : (
                                            <>📋 İçerikleri Seç ve Sırala</>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* İçerik Bitince Durdur */}
                            <div style={{
                                padding: '12px',
                                background: scheduleForm.stop_when_empty ? '#FEF3C7' : 'white',
                                border: scheduleForm.stop_when_empty ? '2px solid #F59E0B' : '1px solid #d1d5db',
                                borderRadius: '8px',
                                transition: 'all 0.2s'
                            }}>
                                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={scheduleForm.stop_when_empty}
                                        onChange={(e) => setScheduleForm({ ...scheduleForm, stop_when_empty: e.target.checked })}
                                        style={{ marginRight: '10px', marginTop: '2px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500', color: '#374151' }}>
                                            ⏹️ İçerik Bitince Otomasyonu Durdur
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                            {scheduleForm.stop_when_empty
                                                ? '✅ Tüm içerikler paylaşıldığında otomasyon otomatik olarak devre dışı kalacak'
                                                : '🔄 Tüm içerikler paylaşıldığında başa dönüp tekrar paylaşılacak (varsayılan)'
                                            }
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* PAYLAŞIM SIKLIĞI */}
                    <div className="automation-form-group">
                        <label className="automation-form-label">
                            Paylaşım Sıklığı
                        </label>
                        <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                            <label className="automation-radio-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="schedule_mode"
                                    value="daily"
                                    checked={scheduleMode === 'daily'}
                                    onChange={(e) => setScheduleMode(e.target.value)}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontWeight: scheduleMode === 'daily' ? 'bold' : 'normal' }}>
                                    📅 Günlük
                                </span>
                            </label>

                            <label className="automation-radio-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="schedule_mode"
                                    value="monthly"
                                    checked={scheduleMode === 'monthly'}
                                    onChange={(e) => setScheduleMode(e.target.value)}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontWeight: scheduleMode === 'monthly' ? 'bold' : 'normal' }}>
                                    📆 Aylık
                                </span>
                            </label>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            💡 {scheduleMode === 'daily'
                                ? 'Her gün belirlediğiniz saatlerde paylaşım yapılır'
                                : 'Her ay belirlediğiniz günlerde paylaşım yapılır'}
                        </div>
                    </div>

                    {/* GÜNLÜK MOD */}
                    {scheduleMode === 'daily' && (
                        <>
                            <div className="automation-form-group">
                                <label className="automation-form-label">
                                    Günde Kaç Kez Paylaşım Yapılsın?
                                </label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <select
                                        value={scheduleForm.times_per_day}
                                        onChange={(e) => updateTimesPerDay(parseInt(e.target.value))}
                                        className="automation-form-select"
                                        style={{ flex: 1 }}
                                    >
                                        {[...Array(10)].map((_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {i + 1} kez
                                            </option>
                                        ))}
                                    </select>

                                    <label className="automation-checkbox-label" style={{ marginBottom: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={autoSort}
                                            onChange={(e) => {
                                                setAutoSort(e.target.checked);
                                                if (e.target.checked) {
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        daily_times: sortTimesByHourMinute(scheduleForm.daily_times)
                                                    });
                                                }
                                            }}
                                            className="automation-checkbox-input"
                                        />
                                        <span className="automation-checkbox-text" style={{ fontSize: '14px' }}>
                                            Saatleri otomatik sırala
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="automation-form-group">
                                <label className="automation-form-label">
                                    Paylaşım Saatleri
                                    <span className="automation-sort-info">
                                        (Her gün bu saatlerde paylaşılır)
                                    </span>
                                </label>
                                <div className="automation-schedule-times-container">
                                    {scheduleForm.daily_times.filter(Boolean).map((time, index) => {

                                        const actualOrder = index + 1;

                                        return (
                                            <div key={index} className="automation-schedule-time-row">
                                                <div className="automation-schedule-time-number">
                                                    {index + 1}.
                                                </div>

                                                <div className="automation-time-input-wrapper">
                                                    <label className="automation-time-label">Saat:</label>
                                                    <input
                                                        type="time"
                                                        value={`${(time?.hour ?? 0).toString().padStart(2, '0')}:${(time?.minute ?? 0).toString().padStart(2, '0')}`}
                                                        onChange={(e) => handleTimeChange(index, e.target.value)}
                                                        onBlur={handleTimeBlur}
                                                        className="automation-time-input"
                                                        required
                                                    />

                                                    <span className="automation-time-hint">
                                                        Her gün bu saatte
                                                    </span>
                                                </div>

                                                <div className="automation-schedule-order-indicator">
                                                    {autoSort && scheduleForm.daily_times.length > 1 && (
                                                        <>
                                                            {index === 0 && (
                                                                <span className="automation-earliest-indicator">🕐 İlk</span>
                                                            )}
                                                            {index === scheduleForm.daily_times.length - 1 && (
                                                                <span className="automation-latest-indicator">🕕 Son</span>
                                                            )}
                                                        </>
                                                    )}


                                                    {!autoSort && scheduleForm.daily_times.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedTimes = scheduleForm.daily_times.filter((_, i) => i !== index);
                                                                setScheduleForm({
                                                                    ...scheduleForm,
                                                                    times_per_day: updatedTimes.length,
                                                                    daily_times: updatedTimes
                                                                });
                                                            }}
                                                            style={{
                                                                padding: '4px 8px',
                                                                background: '#EF4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                            title="Bu saati kaldır"
                                                        >
                                                            Kaldır
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                    {/* AYLIK MOD */}
                    {scheduleMode === 'monthly' && (
                        <div className="automation-form-group">
                            <label className="automation-form-label">
                                📆 Aylık Zamanlamalar
                            </label>

                            <div style={{ marginTop: '10px' }}>
                                {scheduleForm.monthly_schedules.length === 0 ? (
                                    <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        background: '#f9fafb',
                                        borderRadius: '8px',
                                        border: '2px dashed #e5e7eb',
                                        color: '#6b7280',
                                        marginBottom: '15px'
                                    }}>
                                        Henüz aylık zamanlama eklenmedi
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px' }}>
                                        {scheduleForm.monthly_schedules.map((schedule, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    padding: '12px',
                                                    background: '#f9fafb',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e5e7eb'
                                                }}
                                            >
                                                <span style={{ fontWeight: '500', minWidth: '60px' }}>Her ayın</span>

                                                <select
                                                    value={schedule.day}
                                                    onChange={(e) => updateMonthlySchedule(index, 'day', e.target.value)}
                                                    style={{
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d1d5db',
                                                        background: 'white',
                                                        minWidth: '120px'
                                                    }}
                                                >
                                                    {[...Array(31)].map((_, i) => (
                                                        <option key={i + 1} value={i + 1}>
                                                            {i + 1}. günü
                                                        </option>
                                                    ))}
                                                    <option value="32">Son günü</option>
                                                </select>

                                                <span style={{ fontWeight: '500' }}>saat</span>

                                                <input
                                                    type="time"
                                                    value={`${schedule.hour.toString().padStart(2, '0')}:${schedule.minute.toString().padStart(2, '0')}`}
                                                    onChange={(e) => updateMonthlySchedule(index, 'time', e.target.value)}
                                                    className="automation-time-input"
                                                    style={{ minWidth: '100px' }}
                                                    required
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => removeMonthlySchedule(index)}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: '#EF4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        marginLeft: 'auto'
                                                    }}
                                                >
                                                    Kaldır
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={addMonthlySchedule}
                                    style={{
                                        padding: '10px 16px',
                                        background: '#10B981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    ➕ Yeni Zamanlama Ekle
                                </button>

                                <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                                    💡 Not: 31 çekmeyen aylarda, o ayın son günü otomatik olarak seçilir
                                </div>
                            </div>
                        </div>
                    )}

                    {/* BİLDİRİM AYARI */}
                    <div className="automation-form-group">
                        <label className="automation-checkbox-label">
                            <input
                                type="checkbox"
                                checked={scheduleForm.enable_notifications}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, enable_notifications: e.target.checked })}
                                className="automation-checkbox-input"
                            />
                            <span className="automation-checkbox-text">
                                Bildirim gonder (icerik azaldiginda/tukendiginde Telegram'a uyari)
                            </span>
                        </label>
                    </div>

                    {/* Aktiflik Durumu */}
                    <div className="automation-form-group">
                        <label className="automation-checkbox-label">
                            <input
                                type="checkbox"
                                checked={scheduleForm.is_active}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                                className="automation-checkbox-input"
                            />
                            <span className="automation-checkbox-text">
                                Otomasyonu aktif olarak başlat
                            </span>
                        </label>
                    </div>

                    {/* Modal Footer */}
                    <div className="automation-modal-footer">
                        <div className="automation-footer-left">
                            {editingSchedule && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="automation-btn-delete"
                                >
                                    🗑️ Otomasyonu Sil
                                </button>
                            )}
                        </div>

                        <div className="automation-footer-right">
                            <button
                                type="submit"
                                disabled={
                                    loading ||
                                    scheduleForm.tag_ids.length === 0 ||
                                    (scheduleForm.content_order === 'sequential' && contentOrderData.length === 0) ||
                                    (
                                        scheduleForm.channel_ids.length === 0 &&
                                        !(scheduleForm.enable_youtube_upload && scheduleForm.youtube_channel_id) &&
                                        scheduleForm.x_account_ids.length === 0 &&
                                        !(scheduleForm.enable_tiktok_upload && scheduleForm.tiktok_account_ids.length > 0) &&
                                        !(scheduleForm.enable_instagram_upload && scheduleForm.instagram_account_ids.length > 0)
                                    )
                                }
                                className="automation-btn-save"
                            >
                                {loading ? 'Kaydediliyor...' : editingSchedule ? 'Güncelle' : 'Oluştur'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Content Picker Modal */}
            <ContentPickerModal
                isOpen={showContentPicker}
                onClose={() => setShowContentPicker(false)}
                onSave={handleContentOrderSave}
                selectedTagIds={scheduleForm.tag_ids}
                tags={localTags}
                ruleId={editingSchedule?.id}
                existingOrder={orderedContents}
            />
        </div>
    );
}
export default AutomationModal;